import { Stack } from '@mui/material';
import classNames from 'classnames';
import { FieldArray, Form, Formik } from 'formik';
import React, { useCallback } from 'react';
import Pluralize from 'react-pluralize';

import { Select, SelectOption } from '@sbu/components.select';
import { useToasts } from '@sbu/contexts.toasts';
import { DynamicFeatureFlag, useFeatureFlag } from '@sbu/hooks.featureflag';
import { EventPropertyName, TrackEventName } from '@sbu/utils.tracking';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@sumologic/yama-ds-components';

import {
  BulkAddEntityTagsOperationMutation as BulkAddEntityTagsOperation,
  BulkAddEntityTagsOperationMutationVariables as BulkAddEntityTagsOperationVariables,
  BulkRemoveEntityTagsOperationMutation as BulkRemoveEntityTagsOperation,
  BulkRemoveEntityTagsOperationMutationVariables as BulkRemoveEntityTagsOperationVariables,
  BulkUpdateEntityTagsOperationMutation as BulkUpdateEntityTagsOperation,
  BulkUpdateEntityTagsOperationMutationVariables as BulkUpdateEntityTagsOperationVariables,
} from 'core/graphql';
import { TagEditorContainer } from 'shared/components/internal/Tags';
import { useMutation, useTrackEvent } from 'shared/hooks';

import css from './EntitiesTagEditor.module.scss';
import {
  BULK_ADD_ENTITY_TAGS_MUTATION,
  BULK_REMOVE_ENTITY_TAGS_MUTATION,
  BULK_UPDATE_ENTITY_TAGS_MUTATION,
} from './mutations';
import { validationSchema } from './validation';

enum FieldNames {
  MODE = 'mode',
  TAGS = 'tags',
}

export enum ModeValues {
  ADD,
  REMOVE,
  REPLACE,
}

interface FormValues {
  [FieldNames.MODE]: ModeValues;
  [FieldNames.TAGS]: string[];
}

const initialValues = {
  [FieldNames.MODE]: ModeValues.ADD,
  [FieldNames.TAGS]: [],
};

interface EntitiesTagEditorProps {
  entityIds: string[];
  onClose: () => void;
}

export const EntitiesTagEditor = ({
  entityIds,
  onClose,
}: EntitiesTagEditorProps) => {
  const { length } = entityIds;
  const isEntitiesListTableEnabled = useFeatureFlag(
    DynamicFeatureFlag.EnableEntitiesListTable
  );

  const [updateTags, { loading: updateLoading }] = useMutation<
    BulkUpdateEntityTagsOperation,
    BulkUpdateEntityTagsOperationVariables
  >(BULK_UPDATE_ENTITY_TAGS_MUTATION);

  const [addTags, { loading: addLoading }] = useMutation<
    BulkAddEntityTagsOperation,
    BulkAddEntityTagsOperationVariables
  >(BULK_ADD_ENTITY_TAGS_MUTATION);

  const [removeTags, { loading: removeLoading }] = useMutation<
    BulkRemoveEntityTagsOperation,
    BulkRemoveEntityTagsOperationVariables
  >(BULK_REMOVE_ENTITY_TAGS_MUTATION);

  const isUpdating = updateLoading || addLoading || removeLoading;

  const { addSuccess } = useToasts();

  const trackEvent = useTrackEvent();

  const onSubmit = useCallback(
    async ({ mode, tags }: FormValues) => {
      const updateFn = {
        [ModeValues.ADD]: addTags,
        [ModeValues.REMOVE]: removeTags,
        [ModeValues.REPLACE]: updateTags,
      }[mode];

      // Execute request.
      const result = await updateFn?.({ variables: { entityIds, tags } });

      if (result && !result.errors) {
        // Notify user.
        addSuccess('The Entity Tags have been updated successfully.');
      }

      if (isEntitiesListTableEnabled) {
        trackEvent(TrackEventName.ENTITIES_BULK_ACTION, {
          [EventPropertyName.ENTITY_COUNT]: entityIds.length,
          [EventPropertyName.ACTION]: 'ChangeTags',
        });
      }

      // Close the dialog.
      onClose();
    },
    [
      addSuccess,
      addTags,
      entityIds,
      onClose,
      removeTags,
      updateTags,
      isEntitiesListTableEnabled,
      trackEvent,
    ]
  );

  return (
    <Dialog
      data-sumo-test='entities-tag-editor-dialog'
      onClose={onClose}
      open={true}
      maxWidth='sm'
      fullWidth={true}
      aria-labelledby='entities-tag-editor-dialog-title'
    >
      <Formik<FormValues>
        initialValues={initialValues}
        onSubmit={onSubmit}
        validationSchema={validationSchema}
      >
        {({ isValid, setFieldValue }) => (
          <Form>
            <DialogTitle id='entities-tag-editor-dialog-title'>
              <Stack alignItems='center' direction='row' gap={1}>
                <Select
                  defaultValue={ModeValues.ADD}
                  name={FieldNames.MODE}
                  onChange={(e) =>
                    setFieldValue(FieldNames.MODE, e.target.value)
                  }
                  variant='inline'
                >
                  <SelectOption value={ModeValues.ADD}>Add</SelectOption>
                  <SelectOption value={ModeValues.REMOVE}>Remove</SelectOption>
                  <SelectOption value={ModeValues.REPLACE}>
                    Replace
                  </SelectOption>
                </Select>
                Tags for {length.toLocaleString()}{' '}
                <Pluralize
                  count={length}
                  plural='Entities'
                  showCount={false}
                  singular='Entity'
                />
              </Stack>
            </DialogTitle>
            <DialogContent sx={{ width: 500 }}>
              <FieldArray name={FieldNames.TAGS}>
                {({
                  form: {
                    errors,
                    values: {
                      [FieldNames.MODE]: mode,
                      [FieldNames.TAGS]: tags,
                    },
                  },
                  push,
                  remove,
                }) => (
                  <>
                    <TagEditorContainer
                      fullWidth={true}
                      keepFocus={true}
                      onAddTag={push}
                      onRemoveTag={(tag) => {
                        const index = tags.indexOf(tag);
                        index >= 0 && remove<string>(index);
                      }}
                      source='entity'
                      tags={tags}
                    />
                    {!tags.length && mode === ModeValues.REPLACE && (
                      <div className={css.hint}>
                        No Tags are selected. All Tags will be removed from the
                        selected{' '}
                        <Pluralize
                          count={length}
                          plural='Entities'
                          showCount={false}
                          singular='Entity'
                        />
                        .
                      </div>
                    )}
                    {errors[FieldNames.TAGS] && (
                      <div className={classNames(css.hint, css.errorHint)}>
                        <>{errors[FieldNames.TAGS]}</>
                      </div>
                    )}
                  </>
                )}
              </FieldArray>
            </DialogContent>
            <DialogActions>
              <Button color='secondary' onClick={() => onClose()}>
                Cancel
              </Button>
              <Button
                disabled={!isValid || isUpdating}
                loading={isUpdating}
                type='submit'
              >
                Confirm
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};
